import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Stack,
    Avatar,
    alpha,
    useTheme,
    Fade,
    Grow,
    Tooltip
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    HighlightOff as HighlightOffIcon,
    HistoryEdu as TradeIcon,
    CalendarToday as DateIcon,
    Description as OfferIcon
} from '@mui/icons-material';
import { getApplications, updateApplicationStatus, getJobs } from '../Apis/JobApis';
import { generateOfferLetter, issueDeploymentOrder } from '../Apis/IndustryConnectApis';
import { 
    FilterList as FilterIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';

const Applications = () => {
    const theme = useTheme();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [viewProfileOpen, setViewProfileOpen] = useState(false);
    const [currentCandidate, setCurrentCandidate] = useState(null);
    const [offerDetails, setOfferDetails] = useState({ 
        remarks: '', 
        salary: '', 
        joiningDate: new Date().toISOString().split('T')[0] 
    });
    
    // Deployment State
    const [deployDialogOpen, setDeployDialogOpen] = useState(false);
    const [deployNotes, setDeployNotes] = useState('');

    // Filter State
    const [jobs, setJobs] = useState([]);
    const [filters, setFilters] = useState({
        jobId: '',
        status: '',
        search: ''
    });
    const [searchValue, setSearchValue] = useState('');

    const fetchJobs = async () => {
        try {
            const res = await getJobs();
            if (res.success) {
                setJobs(res.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch jobs:", error);
        }
    };

    const fetchApplications = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.jobId) params.jobId = filters.jobId;
            if (filters.status) params.status = filters.status;
            if (filters.search) params.search = filters.search;

            const res = await getApplications(params);
            if (res.success) {
                const applicationData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setApplications(applicationData);
            }
        } catch (error) {
            console.error("Failed to fetch applications:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    useEffect(() => {
        fetchApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.jobId, filters.status, filters.search]);

    useEffect(() => {
        // Skip redundant update that causes double fetch
        if (searchValue === filters.search) return;

        const timer = setTimeout(() => {
            setFilters(prev => ({ ...prev, search: searchValue }));
        }, 500);
        return () => clearTimeout(timer);
    }, [searchValue, filters.search]);

    const handleStatusUpdate = async (id, status) => {
        try {
            const res = await updateApplicationStatus(id, { status });
            if (res.success) {
                fetchApplications();
            }
        } catch (error) {
            console.error("Failed to update status:", error);
        }
    };

    const handleOfferClick = (app) => {
        setSelectedApp(app);
        setOpenDialog(true);
    };

    const handleDeployClick = (app) => {
        setSelectedApp(app);
        setDeployDialogOpen(true);
    };

    const handleConfirmDeployment = async () => {
        try {
            const res = await issueDeploymentOrder(selectedApp.id, { remarks: deployNotes });
            if (res.success) {
                setDeployDialogOpen(false);
                setDeployNotes('');
                fetchApplications();
            }
        } catch (error) {
            console.error("Deployment failed:", error);
        }
    };

    const handleViewProfile = (app) => {
        setCurrentCandidate(app.student);
        setViewProfileOpen(true);
    };

    const handleGenerateOffer = async () => {
        try {
            const res = await generateOfferLetter(selectedApp.id, offerDetails);
            if (res.success) {
                setOpenDialog(false);
                fetchApplications();
            }
        } catch (error) {
            console.error("Failed to generate offer:", error);
        }
    };

    if (loading) return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2 }}>
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" color="#64748B" fontWeight="bold">Loading candidates...</Typography>
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
            <Fade in={true} timeout={600}>
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="1000" color="#0F172A" sx={{ letterSpacing: -1 }}>Talent Pipeline</Typography>
                        <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>Manage and review student applications for industrial opportunities.</Typography>
                    </Box>
                    
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1 }}>
                            <FilterIcon sx={{ color: '#64748B', fontSize: 20 }} />
                            <Typography variant="body2" fontWeight="800" color="#475569">FILTERS</Typography>
                        </Stack>

                        <TextField
                            size="small"
                            placeholder="Find Candidate..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />

                        <TextField
                            select
                            size="small"
                            label="Filter by Job"
                            value={filters.jobId}
                            onChange={(e) => setFilters({ ...filters, jobId: e.target.value })}
                            sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        >
                            <MenuItem value="">All Jobs</MenuItem>
                            {jobs.map((job) => (
                                <MenuItem key={job.id} value={job.id}>{job.title}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            size="small"
                            label="Status"
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="applied">Applied</MenuItem>
                            <MenuItem value="reviewing">Reviewing</MenuItem>
                            <MenuItem value="shortlisted">Shortlisted</MenuItem>
                            <MenuItem value="selected">Selected</MenuItem>
                            <MenuItem value="offered">Offered</MenuItem>
                            <MenuItem value="hired">Hired</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </TextField>

                        <Tooltip title="Reset Filters">
                            <IconButton 
                                onClick={() => {
                                    setFilters({ jobId: '', status: '', search: '' });
                                    setSearchValue('');
                                }}
                                size="small"
                                sx={{ bgcolor: alpha('#64748B', 0.05) }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Paper>
                </Box>
            </Fade>

            <Grow in={true} timeout={800}>
                <TableContainer component={Paper} elevation={0} sx={{
                    borderRadius: 5,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.03)',
                    overflow: 'hidden'
                }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', fontSize: '0.75rem' }}>CANDIDATE</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', fontSize: '0.75rem' }}>COLLEGE</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', fontSize: '0.75rem' }}>OPPORTUNITY / TRADE</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', fontSize: '0.75rem' }}>SUBMITTED</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', fontSize: '0.75rem' }}>STATUS</TableCell>
                                <TableCell sx={{ fontWeight: '800', color: '#475569', fontSize: '0.75rem' }} align="center">ACTIONS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {applications.map((app) => (
                                <TableRow key={app.id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 'bold' }}>
                                                {app.student?.studentName?.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight="700" color="#1E293B">{app.student?.studentName}</Typography>
                                                <Typography variant="caption" color="#64748B">{app.student?.email}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600" color="#475569">
                                            {app.student?.college?.name || 'DIRECT CANDIDATE'}
                                        </Typography>
                                        <Typography variant="caption" color="#94A3B8">
                                            {app.student?.college?.code || ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600" color="#334155">{app.job?.title}</Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <TradeIcon sx={{ fontSize: 12, color: '#94A3B8' }} />
                                            <Typography variant="caption" color="#94A3B8" fontWeight="bold">
                                                {app.student?.itiTradeMapping?.trade?.name || 'GENERIC'}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <DateIcon sx={{ fontSize: 14, color: '#64748B' }} />
                                            <Typography variant="body2" color="#64748B">{new Date(app.createdAt).toLocaleDateString()}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={app.status.toUpperCase()}
                                            size="small"
                                            sx={{
                                                fontWeight: '900',
                                                fontSize: '0.6rem',
                                                bgcolor: app.status === 'selected' || app.status === 'hired' ? alpha(theme.palette.success.main, 0.1) : app.status === 'rejected' ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.primary.main, 0.1),
                                                color: app.status === 'selected' || app.status === 'hired' ? theme.palette.success.main : app.status === 'rejected' ? theme.palette.error.main : theme.palette.primary.main,
                                                borderRadius: '6px'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                            <Tooltip title="View Profile">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#6366F1', '&:hover': { bgcolor: alpha('#6366F1', 0.1) } }}
                                                    onClick={() => handleViewProfile(app)}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {['applied', 'reviewing'].includes(app.status?.toLowerCase()) && (
                                                <Tooltip title="Shortlist">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#10B981', '&:hover': { bgcolor: alpha('#10B981', 0.1) } }}
                                                        onClick={() => handleStatusUpdate(app.id, 'shortlisted')}
                                                    >
                                                        <CheckCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {app.status?.toLowerCase() === 'selected' ? (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="success"
                                                    onClick={() => handleDeployClick(app)}
                                                    sx={{ 
                                                        textTransform: 'none', 
                                                        px: 2, 
                                                        fontWeight: 'bold',
                                                        borderRadius: 2,
                                                        boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)'
                                                    }}
                                                >
                                                    Issue Deployment Order
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={['offered', 'hired', 'rejected'].includes(app.status?.toLowerCase())}
                                                    startIcon={<OfferIcon />}
                                                    onClick={() => handleOfferClick(app)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        px: 2,
                                                        bgcolor: '#0F172A',
                                                        borderRadius: 2,
                                                        fontWeight: 'bold',
                                                        boxShadow: 'none',
                                                        '&:hover': { bgcolor: '#1E293B', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)' }
                                                    }}
                                                >
                                                    {app.status?.toLowerCase() === 'offered' ? 'Offer Sent' : 'Issue Offer'}
                                                </Button>
                                            )}
                                            <Tooltip title="Reject">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#F43F5E', '&:hover': { bgcolor: alpha('#F43F5E', 0.1) } }}
                                                    onClick={() => handleStatusUpdate(app.id, 'rejected')}
                                                >
                                                    <HighlightOffIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grow>

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                PaperProps={{
                    sx: { borderRadius: 5, p: 2, width: '100%', maxWidth: 450 }
                }}
            >
                <DialogTitle sx={{ fontWeight: '1000', fontSize: '1.25rem', pb: 1 }}>Compose Offer</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3, color: '#64748B' }}>
                        You are initiating an official offer for <strong style={{ color: '#0F172A' }}>{selectedApp?.student?.studentName}</strong>.
                        This will transition their status to Offered.
                    </Typography>
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Salary / Stipend (₹)"
                            placeholder="Enter annual package or monthly stipend"
                            value={offerDetails.salary}
                            onChange={(e) => setOfferDetails({ ...offerDetails, salary: e.target.value })}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label="Tentative Joining Date"
                            InputLabelProps={{ shrink: true }}
                            value={offerDetails.joiningDate}
                            onChange={(e) => setOfferDetails({ ...offerDetails, joiningDate: e.target.value })}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Offer Remarks / Notes"
                            placeholder="Detail any specific instructions or welcome message..."
                            value={offerDetails.remarks}
                            onChange={(e) => setOfferDetails({ ...offerDetails, remarks: e.target.value })}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 3 }
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpenDialog(false)} sx={{ fontWeight: 'bold', color: '#64748B' }}>Discard</Button>
                    <Button
                        onClick={handleGenerateOffer}
                        variant="contained"
                        sx={{
                            bgcolor: '#0F172A',
                            borderRadius: 2,
                            px: 3,
                            fontWeight: 'bold',
                            '&:hover': { bgcolor: '#1E293B' }
                        }}
                    >
                        Send Offer
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Candidate Profile Dialog */}
            <Dialog
                open={viewProfileOpen}
                onClose={() => setViewProfileOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 5, p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: '1000', fontSize: '1.4rem', borderBottom: '1px solid #F1F5F9', mb: 2 }}>
                    Candidate Profile
                </DialogTitle>
                <DialogContent>
                    {currentCandidate && (
                        <Box>
                            <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 4 }}>
                                <Avatar
                                    src={currentCandidate.photo}
                                    sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 'bold' }}
                                >
                                    {currentCandidate.studentName?.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="800" color="#0F172A">{currentCandidate.studentName}</Typography>
                                    <Typography variant="body2" color="#64748B">{currentCandidate.email} • {currentCandidate.mobileNumber}</Typography>
                                    <Chip
                                        label={currentCandidate.itiTradeMapping?.trade?.name || 'Technical Candidate'}
                                        size="small"
                                        sx={{ mt: 1, fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.05), color: theme.palette.primary.main, borderRadius: 2 }}
                                    />
                                </Box>
                            </Stack>

                            <Typography variant="subtitle2" fontWeight="900" color="#475569" sx={{ mb: 2, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                Academic Details
                            </Typography>

                            <Paper variant="outlined" sx={{ borderRadius: 4, p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="bold">HALL TICKET NO</Typography>
                                        <Typography variant="body2" fontWeight="700" color="#1E293B">{currentCandidate.hallTicketNumber || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="bold">INSTITUTION</Typography>
                                        <Typography variant="body2" fontWeight="700" color="#1E293B">{currentCandidate.college?.name || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="bold">BATCH / SECTION</Typography>
                                        <Typography variant="body2" fontWeight="700" color="#1E293B">{currentCandidate.batchName || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="bold">TRADE YEAR</Typography>
                                        <Typography variant="body2" fontWeight="700" color="#1E293B">{currentCandidate.yearName || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="bold">ADMISSION DATE</Typography>
                                        <Typography variant="body2" fontWeight="700" color="#1E293B">
                                            {currentCandidate.admissionDate ? new Date(currentCandidate.admissionDate).toLocaleDateString() : 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="bold">GENDER</Typography>
                                        <Typography variant="body2" fontWeight="700" color="#1E293B" sx={{ textTransform: 'capitalize' }}>{currentCandidate.gender || 'N/A'}</Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        fullWidth
                        onClick={() => setViewProfileOpen(false)}
                        variant="contained"
                        sx={{ bgcolor: '#0F172A', borderRadius: 3, py: 1.5, fontWeight: 'bold' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Deployment Order Dialog */}
            <Dialog
                open={deployDialogOpen}
                onClose={() => setDeployDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: 5, p: 2, width: '100%', maxWidth: 450 }
                }}
            >
                <DialogTitle sx={{ fontWeight: '1000', fontSize: '1.25rem', pb: 1, color: '#10B981' }}>
                    Issue Deployment Order
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3, color: '#64748B' }}>
                        Confirm final placement for <strong style={{ color: '#0F172A' }}>{selectedApp?.student?.studentName}</strong>. 
                        This marks the candidate as <strong>Hired</strong> and initiates training phase.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Final Joining Instructions"
                        placeholder="Provide reporting location, person of contact, and any critical pre-joining items..."
                        value={deployNotes}
                        onChange={(e) => setDeployNotes(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 3 }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeployDialogOpen(false)} sx={{ fontWeight: 'bold', color: '#64748B' }}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDeployment}
                        variant="contained"
                        sx={{
                            bgcolor: '#10B981',
                            borderRadius: 2,
                            px: 3,
                            fontWeight: 'bold',
                            '&:hover': { bgcolor: '#059669' }
                        }}
                    >
                        Confirm Deployment
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Applications;
